import { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { trackRemoveFromCart } from '../../utils/ga4';
import {
  ArrowRight, MapPin, Calendar, Users, ChevronDown,
  Loader2, Package, Sparkles, Trophy, Heart, Award, Clock, TreePine,
  CheckCircle2, Send, MessageSquare, Microscope, Code, Bot,
  Wrench, Calculator, FlaskConical, Cpu, Cog, Brain, BookOpen,
  Shield, Star, Zap, GraduationCap, Tag, Percent, Gift, Baby, Search, Flame, Info, Plus, X, ShoppingCart
} from 'lucide-react';
import axios from 'axios';
import ParentConciergeModal from '../../components/camps/ParentConciergeModal';
import CampExploreHero from '../../components/camps/CampExploreHero';
import { useCourseDetailModal, CourseDetailModal } from '../../components/CourseDetailModal';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import MultiWeekCartModal from '../../components/camps/MultiWeekCartModal';
import { DEFAULT_TIERS, tierFor, countSessions } from '../../utils/campPricing';

import { testimonials, overallRating } from '../../data/testimonials';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_CONFIG = {
  science: { label: 'Science', color: 'from-rose-500 to-pink-400', bg: 'bg-rose-500/15', text: 'text-rose-400', icon: FlaskConical },
  coding: { label: 'Technology', color: 'from-blue-500 to-indigo-400', bg: 'bg-blue-500/15', text: 'text-blue-400', icon: Code },
  technology: { label: 'Technology', color: 'from-blue-500 to-indigo-400', bg: 'bg-blue-500/15', text: 'text-blue-400', icon: Code },
  robotics: { label: 'Robotics', color: 'from-orange-500 to-amber-400', bg: 'bg-orange-500/15', text: 'text-orange-400', icon: Bot },
  engineering: { label: 'Engineering', color: 'from-emerald-500 to-teal-400', bg: 'bg-emerald-500/15', text: 'text-emerald-400', icon: Cog },
  math: { label: 'Math', color: 'from-purple-500 to-violet-400', bg: 'bg-purple-500/15', text: 'text-purple-400', icon: Calculator },
};
// Weekly STEM rotation — Mon Science → Tue Tech → Wed Engineering → Thu Math → Fri Robotics.
// Used as a fallback when an individual day's session_category isn't set, so the header
// theme badge is always populated for a standard Full-Day camp week.
const DAY_THEME = {
  1: 'science',     // Monday
  2: 'technology',  // Tuesday
  3: 'engineering', // Wednesday
  4: 'math',        // Thursday
  5: 'robotics',    // Friday
};
const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

/**
 * Format a time string like "09:00:00" (24h) into a human label "9am".
 * Returns '' for falsy input. Used to render dynamic AM/PM time-ranges
 * on the schedule grid based on each CampDay's start_time / end_time.
 */
const formatTime = (t) => {
  if (!t) return '';
  const m = String(t).match(/^(\d{1,2}):(\d{2})/);
  if (!m) return String(t);
  let h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  const period = h >= 12 ? 'pm' : 'am';
  h = h % 12;
  if (h === 0) h = 12;
  return min === 0 ? `${h}${period}` : `${h}:${String(min).padStart(2, '0')}${period}`;
};

/** Render a day's session time-range, e.g. "9am–12pm". Falls back to a
 * sensible default based on session_type when the day didn't store times. */
const formatSessionRange = (day) => {
  if (!day) return '';
  if (day.start_time && day.end_time) {
    return `${formatTime(day.start_time)}–${formatTime(day.end_time)}`;
  }
  if (day.session_type === 'am') return '9am–12pm';
  if (day.session_type === 'pm') return '1pm–4pm';
  return '9am–4pm';
};

const HERO_STATS_FULL = [
  { value: '8', label: 'Weeks of Camp', icon: Calendar },
  { value: '5', label: 'STEM Pillars Daily', icon: Sparkles },
  { value: '3', label: 'Grade Bands', icon: Users },
  { value: 'Top 5%', label: 'STEM.org Accredited', icon: Award },
];

const HERO_STATS_HALF = [
  { value: '8', label: 'Weeks of Camp', icon: Calendar },
  { value: '5', label: 'STEM Pillars Daily', icon: Sparkles },
  { value: '1', label: 'Grade Band (K-2)', icon: Users },
  { value: 'Top 5%', label: 'STEM.org Accredited', icon: Award },
];

export default function SummerCampsPage() {
  const navigate = useNavigate();
  const [camps, setCamps] = useState([]);
  const [campDetails, setCampDetails] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const weekParam = searchParams.get('week');
  const gradeParam = searchParams.get('grade');
  const fromExplore = searchParams.get('from');
  // Use the shared hook so this page's course modal matches the one on /register
  // (same hero image overlay, Sample Activities + What's in the Kit columns, topic pills).
  const courseDetailModal = useCourseDetailModal();
  
  // Multi-select filters
  const [gradeFilters, setGradeFilters] = useState(() => {
    // Initial grade band — honors ?grade=K-2|3-5|6-8 param, defaults to 3-5 (the
    // largest cohort in our current catalog).
    const g = new URLSearchParams(window.location.search).get('grade');
    return ['K-2', '3-5', '6-8'].includes(g) ? [g] : ['3-5'];
  }); // K-2, 3-5, 6-8
  const [showPricing, setShowPricing] = useState(false);

  // Derived values
  const gradeFilter = gradeFilters[0] || '3-5'; // Use first selected grade for display
  
  // Parent Concierge state
  const [conciergeOpen, setConciergeOpen] = useState(false);
  const [conciergeMatches, setConciergeMatches] = useState([]);
  const [conciergeActive, setConciergeActive] = useState(false);

  // Multi-week cart modal — opens from the yellow pending bar once the parent
  // has accumulated session selections across one or more camp weeks. Collects
  // learner / extended care / coupon and submits every pending week at once.
  const [cartModalOpen, setCartModalOpen] = useState(false);

  // Per-camp selected sessions — owned here so the on-card checkboxes (in the
  // schedule grid) and the panel's day selector share one source of truth.
  // shape: { [campId]: { [dayNumber]: 'am' | 'pm' | 'full_day' } }
  const [campSessions, setCampSessions] = useState({});

  // Per-camp selected LOCATION (sectionId). Drives the chip bar above each
  // camp's schedule grid and is preset into the inline panel as defaultSectionId.
  // shape: { [campId]: sectionId }
  const [selectedLocationByCamp, setSelectedLocationByCamp] = useState({});

  // Global default-location preference. When set (e.g. 'Naperville'), every
  // camp's chip bar will preselect the matching section if available. Lets
  // a parent who knows they want Aurora set it ONCE at the top instead of
  // clicking it on every week. Disabled chips: when a location isn't valid
  // for the active gradeFilter (K-2 only runs at Naperville/Aurora), the
  // chip is greyed out and can't be selected. Stored as the LOCATION NAME
  // (not section id) since each week has a different section id for the
  // same physical location.
  const [globalDefaultLocation, setGlobalDefaultLocation] = useState(null);

  // Read the cart so we can mark sessions that the parent has already added
  // to their cart. After they "Add to Cart" we clear campSessions (so they
  // don't accidentally re-add the same selection), but those same cards
  // re-light up here as "✓ In Cart" — preventing the "did my add work?"
  // confusion when they click "Continue Shopping".
  // Shape: { [campId]: { [dayNumber]: 'am' | 'pm' | 'full_day' } }
  const { cart, refreshCart, removeCampFromCart } = useCart();
  const { api } = useAuth();
  const cartedSessionsByCamp = useMemo(() => {
    const out = {};
    (cart?.items || []).forEach(item => {
      if (item.type !== 'camp' || !item.camp_id) return;
      const cid = item.camp_id;
      out[cid] = out[cid] || {};
      (item.selected_sessions || []).forEach(s => {
        const d = parseInt(s.day);
        const next = s.session;
        const cur = out[cid][d];
        if (!cur) {
          out[cid][d] = next;
        } else if (cur === 'full_day' || next === 'full_day' || (cur === 'am' && next === 'pm') || (cur === 'pm' && next === 'am')) {
          out[cid][d] = 'full_day';
        }
      });
    });
    return out;
  }, [cart?.items]);

  // Remove a single day's session from the cart for a camp directly from the
  // schedule grid's "✕" on the In-Cart pill. The /api/camps/{id}/add-to-cart/
  // endpoint is idempotent (matches on camp_section_id + learner) and replaces
  // the existing item with the new sessions list — so we just POST the
  // remaining sessions. If nothing remains we drop the whole camp item.
  // `side` is 'am' | 'pm' | 'full_day' (which side of the day the user clicked).
  const removeOneDayFromCart = async (camp, dayNum, side) => {
    const item = (cart?.items || []).find(it => it.type === 'camp' && it.camp_id === camp.id);
    if (!item) return;
    // dayNum can be a string when it comes from Object.entries (in the JSX
    // grouping), and s.day from the cart is a number. Coerce both sides
    // through parseInt for a reliable equality check.
    const targetDay = parseInt(dayNum);
    // Build the new sessions list from the existing one, applying the removal rule.
    const next = [];
    (item.selected_sessions || []).forEach(s => {
      const d = parseInt(s.day);
      if (d !== targetDay) { next.push({ day: d, session: s.session }); return; }
      // This is the day to modify
      if (s.session === side) return; // exact match — drop entirely
      if (s.session === 'full_day' && side === 'am') { next.push({ day: d, session: 'pm' }); return; }
      if (s.session === 'full_day' && side === 'pm') { next.push({ day: d, session: 'am' }); return; }
      if (side === 'full_day') return; // user wants the whole day gone — drop
      // No match — keep the original
      next.push({ day: d, session: s.session });
    });

    try {
      // GA4: fire remove_from_cart BEFORE the network call so we don't lose
      // the event if the request races. Quantity is 2 for a full_day removal.
      try {
        trackRemoveFromCart(
          {
            id: camp.id,
            name: camp.title || camp.name,
            price: 69,
            category: 'summer_camp',
            location: item.location_name || null,
            grade_band: camp.grade_bands?.[0] || camp.grade_band,
          },
          { quantity: side === 'full_day' ? 2 : 1 },
        );
      } catch { /* analytics never breaks UX */ }
      if (next.length === 0) {
        await removeCampFromCart(item.camp_section_id, item.learner_id || null, item.guest_student_name || null);
      } else {
        await api.post(`/camps/${camp.id}/add-to-cart/`, {
          camp_section_id: item.camp_section_id,
          learner_id: item.learner_id || null,
          selected_days: next,
          extended_care: item.extended_care || 'none',
          coupon_code: item.coupon_code || undefined,
        });
        await refreshCart();
        toast.success('Removed from cart');
      }
    } catch (err) {
      toast.error('Failed to remove — please try again');
    }
  };

  // Helper — returns the chip-selected section for a camp, falling back to:
  //   1. the global default-location's matching section (if user set one
  //      via the page-level location picker)
  //   2. first open-to-all section with available spots
  //   3. first section.
  // Also filters out YMCA sections on non-weekend camps (YMCA is weekend-only).
  const getSelectedSectionForCamp = (camp) => {
    const sectionsArr = (campDetails[camp.id]?.sections || []).filter(s =>
      camp.subcategory === 'Weekend Classes' || !s.location_name?.includes('YMCA'),
    );
    if (sectionsArr.length === 0) return null;
    const chosenId = selectedLocationByCamp[camp.id];
    const chosen = sectionsArr.find(s => s.id === chosenId);
    if (chosen) return chosen;
    // Honor the global default if the camp has that location available.
    if (globalDefaultLocation) {
      const matched = sectionsArr.find(s => s.location_name?.includes(globalDefaultLocation));
      if (matched && (matched.spots_remaining ?? 1) > 0) return matched;
    }
    const isOpen = (s) => !(s.location_name?.includes('Bednarcik') || s.location_name?.includes('Plank') || (s.location_eligibility && s.location_eligibility !== 'open'));
    return (
      sectionsArr.find(s => isOpen(s) && (s.spots_remaining ?? 1) > 0) ||
      sectionsArr.find(s => (s.spots_remaining ?? 1) > 0) ||
      sectionsArr[0]
    );
  };

  // When a parent picks a different location chip, update state. If sessions
  // were already selected for days that don't exist on the new location
  // (e.g. day 5 when switching to Bednarcik/Plank Mon-Thu), drop them and
  // notify the parent. The toast and the "had-day-5" check happen OUTSIDE
  // the setCampSessions updater so the reducer stays pure (React 18
  // StrictMode runs reducers twice in dev — side-effects must live outside).
  const setSelectedLocation = (camp, sectionId) => {
    const sec = (campDetails[camp.id]?.sections || []).find(s => s.id === sectionId);
    if (!sec) return;
    setSelectedLocationByCamp(prev => ({ ...prev, [camp.id]: sectionId }));

    const is4Day = sec.location_name?.includes('Bednarcik') || sec.location_name?.includes('Plank');
    if (!is4Day) return;
    const hadFriday = !!(campSessions[camp.id]?.[5]);
    if (!hadFriday) return;

    setCampSessions(prev => {
      const cur = prev[camp.id] || {};
      const { 5: _drop, ...rest } = cur;
      return { ...prev, [camp.id]: rest };
    });
    toast.info('Friday selection removed — this location runs Mon–Thu.');
  };

  const setCampSessionsFor = (campId, updater) => {
    setCampSessions(prev => {
      const cur = prev[campId] || {};
      const next = typeof updater === 'function' ? updater(cur) : updater;
      // Drop empty entries to keep state clean
      if (next && Object.keys(next).length === 0) {
        const { [campId]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [campId]: next };
    });
  };

  // Toggling a single AM or PM card in the schedule grid:
  //   none           → that session
  //   same session   → cleared
  //   other half     → upgrade to 'full_day'
  //   'full_day'     → downgrade to the OTHER half
  const toggleHalfSession = (camp, dayNum, side /* 'am' | 'pm' */) => {
    const otherSide = side === 'am' ? 'pm' : 'am';

    setCampSessionsFor(camp.id, (cur) => {
      const next = { ...cur };
      const current = cur[dayNum];
      if (!current) {
        next[dayNum] = side;
      } else if (current === side) {
        delete next[dayNum];
      } else if (current === otherSide) {
        next[dayNum] = 'full_day';
      } else {
        // current === 'full_day' → keep the OTHER half only
        next[dayNum] = otherSide;
      }
      return next;
    });
  };

  const toggleFullDay = (camp, dayNum) => {
    setCampSessionsFor(camp.id, (cur) => {
      const next = { ...cur };
      if (cur[dayNum] === 'full_day') delete next[dayNum];
      else next[dayNum] = 'full_day';
      return next;
    });
  };

  // Seed campSessions from a tier chip / preset (full_week / half_day /
  // full_day / a single day). Selections accumulate across weeks into
  // campSessions; the yellow pending bar totals them and the MultiWeekCartModal
  // submits everything at once. (No inline panel — that flow was replaced by
  // the cross-week accumulation cart.)
  const seedCampSelection = ({ campId, sectionId, presetType, presetDay, presetSession }) => {
    // If caller didn't pass a sectionId, use the chip-selected location.
    let resolvedSectionId = sectionId;
    if (!resolvedSectionId) {
      const sectionsArr = campDetails[campId]?.sections || [];
      const camp = sectionsArr.length > 0 ? { id: campId } : null;
      if (camp) {
        resolvedSectionId = getSelectedSectionForCamp(camp)?.id;
      }
    }

    if (presetType === 'full_week' || presetType === 'half_day' || presetType === 'full_day') {
      const detail = campDetails[campId];
      const sec = detail?.sections?.find(s => s.id === resolvedSectionId) || detail?.sections?.[0];
      const is4DaySite =
        sec?.location_name?.includes('Bednarcik') || sec?.location_name?.includes('Plank');
      const isWeekendSec = sec?.location_name?.includes('YMCA');
      const maxDay = isWeekendSec ? 2 : (is4DaySite ? 4 : 5);
      const allDays = Array.from(new Set((detail?.days || []).map(d => d.day_number)))
        .filter(n => n >= 1 && n <= maxDay);
      // Seed all available weekdays at the requested tier:
      //   half_day → AM-only Mon–Fri (parent gets exactly the half-day week)
      //   full_day → Mon–Fri full days (the typical "send my kid all day every day" parent)
      //   full_week → same as full_day (kept as alias)
      const sessionForTier = presetType === 'half_day' ? 'am' : 'full_day';
      const seeded = {};
      allDays.forEach(n => { seeded[n] = sessionForTier; });
      setCampSessionsFor(campId, seeded);
    } else if (presetType === 'day' && presetDay) {
      const dayNum = parseInt(presetDay);
      const sess = presetSession === 'am' || presetSession === 'pm' ? presetSession : 'full_day';
      setCampSessionsFor(campId, { [dayNum]: sess });
    }
  };

  // Toggle filter selection
  const toggleGradeFilter = (filter) => {
    // Single-select for the visible pill group — picking a grade band REPLACES
    // the current filter (parents pick one band for their student). All camps
    // for that grade band are shown; AM/PM/Full-Day choice now happens on each
    // camp card directly (no top-level format toggle).
    setGradeFilters([filter]);
  };

  const handleConciergeResults = (matches, preferences) => {
    setConciergeMatches(matches);
    setConciergeActive(true);
    toast.success(`Showing ${matches.length} camps that match your preferences!`);
    
    // Scroll to camps section
    setTimeout(() => {
      document.getElementById('camps-section')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const clearConciergeFilter = () => {
    setConciergeActive(false);
    setConciergeMatches([]);
    toast.success('Showing all camps');
  };

  useEffect(() => {
    axios.get(`${API_URL}/api/camps/?page_size=100`)
      .then(async (res) => {
        const data = res.data.results || res.data || [];
        setCamps(data);
        // Load camp details in parallel (much faster on mobile)
        const detailPromises = data.map(camp =>
          axios.get(`${API_URL}/api/camps/${camp.id}/`)
            .then(res => ({ id: camp.id, data: res.data }))
            .catch(() => null)
        );
        const results = await Promise.all(detailPromises);
        const details = {};
        for (const r of results) {
          if (r) details[r.id] = r.data;
        }
        setCampDetails(details);

        // Restore prior selection saved before login/register, if any
        let pendingCtx = null;
        try {
          const raw = localStorage.getItem('pendingCampRegistration');
          if (raw) pendingCtx = JSON.parse(raw);
        } catch {}

        // Auto-open registration if ?register= param OR a pending context exists
        // (also accepts ?autoOpenCamp= for the /register → /summer-camps redirect path)
        const sp = new URLSearchParams(window.location.search);
        const registerParam = sp.get('autoOpenCamp') || sp.get('register') || pendingCtx?.campId;
        if (registerParam) {
          // Try matching by camp ID first (from camp context redirect)
          const matchedCamp = data.find(c => c.id === registerParam);
          if (matchedCamp && details[matchedCamp.id]) {
            const campDetail = details[matchedCamp.id];
            const savedSection = pendingCtx?.sectionId ? campDetail.sections?.find(s => s.id === pendingCtx.sectionId) : null;
            const section = savedSection || campDetail.sections?.[0];
            const dayFromUrl = sp.get('day') || pendingCtx?.preSelectedDay;
            const sessionFromUrl = sp.get('session');
            const presetFromUrl = sp.get('preset'); // half_day | full_day | full_week

            seedCampSelection({
              campId: matchedCamp.id,
              sectionId: sp.get('section') || section?.id,
              presetType: presetFromUrl || (dayFromUrl ? 'day' : null),
              presetDay: dayFromUrl ? parseInt(dayFromUrl) : undefined,
              presetSession: sessionFromUrl || undefined,
            });

            setTimeout(() => document.getElementById(`camp-${matchedCamp.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 500);
            if (pendingCtx) localStorage.removeItem('pendingCampRegistration');
          } else {
            // Fall back to matching by course title
            const courseTitle = decodeURIComponent(registerParam);
            for (const camp of data) {
              const campDetail = details[camp.id];
              if (!campDetail) continue;
              const matchDay = (campDetail.days || []).find(d => d.course_title === courseTitle);
              if (matchDay && campDetail.sections?.length > 0) {
                seedCampSelection({
                  campId: camp.id,
                  sectionId: campDetail.sections[0].id,
                  presetType: 'day',
                  presetDay: matchDay.day_number,
                });
                setTimeout(() => document.getElementById(`camp-${camp.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 500);
                break;
              }
            }
          }
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  // Auto-scroll to browse section when coming from course detail page
  useEffect(() => {
    if (tabParam || weekParam) {
      setTimeout(() => {
        document.getElementById('browse-camps')?.scrollIntoView({ behavior: 'smooth' });
      }, 500);
    }
  }, [tabParam, weekParam, loading]);

  // Filter camps by format (full-day vs half-day) and grade band
  const filteredCamps = camps.filter(c => {
    // If Parent Concierge is active, only show matched camps
    if (conciergeActive && conciergeMatches.length > 0) {
      return conciergeMatches.some(match => match.camp_id === c.id);
    }

    const bands = c.grade_bands || [];
    const detail = campDetails[c.id];
    const sections = detail?.sections || [];

    // Format filter REMOVED (May 15, 2026) — parents now select AM/PM/Full-Day
    // per-day on the camp card, not as a global page filter. Both half-day-named
    // and full-day-named camps surface together for every grade band.
    const isWeekendCamp = c.subcategory === 'Weekend Classes';
    // (Weekend camps were the only carve-out for the old format toggle; they're
    // unconditionally listed below alongside the rest.)
    void isWeekendCamp;

    // Grade filter
    if (gradeFilter === 'K-2' && !bands.includes('K-2')) return false;
    if (gradeFilter === '3-5' && !bands.includes('3-5')) return false;
    if (gradeFilter === '6-8' && !bands.includes('6-8')) return false;
    return true;
  }).sort((a, b) => {
    const aDetail = campDetails[a.id];
    const bDetail = campDetails[b.id];
    const aDate = aDetail?.sections?.[0]?.start_date || '';
    const bDate = bDetail?.sections?.[0]?.start_date || '';
    return aDate.localeCompare(bDate);
  });

  // Open the shared modal (handles UUID fetch + lessons under the hood)
  const openCourseDetail = (courseId) => courseDetailModal.openCourseDetail(courseId);

  const handleCourseRegister = (course) => {
    // Find the camp + section that contains this course
    for (const camp of camps) {
      const detail = campDetails[camp.id];
      if (!detail) continue;
      const days = detail.days || [];
      const matchingDay = days.find(d => d.course === course.id || d.course_title === course.title);
      if (matchingDay) {
        const sections = detail.sections || [];
        if (sections.length > 0) {
          seedCampSelection({
            campId: camp.id,
            sectionId: sections[0].id,
            presetType: 'day',
            presetDay: matchingDay.day_number,
          });
          setTimeout(() => document.getElementById(`camp-${camp.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
          return;
        }
      }
    }
    // Fallback: navigate to summer camps page
    window.location.href = '/summer-camps';
  };

  return (
    <TooltipProvider delayDuration={200}>
    <div data-testid="summer-camps-page">
      {/* ═══════════ LEAD CAPTURE HERO (first thing parents see) ═══════════ */}
      <CampExploreHero />

      {/* ═══════════ BROWSE CAMPS ═══════════ */}
      <section id="browse-camps" className="relative py-8 bg-gradient-to-b from-rsc-navy to-[#0f2847]">
        <div className="container-app">
          <div className="text-center mb-4">
            <h2 className="text-xl sm:text-2xl font-extrabold text-white mb-1">Weekly <span className="text-rsc-yellow">Schedules</span></h2>
            <p className="text-white/40 text-sm">Showing {filteredCamps.length} camp{filteredCamps.length !== 1 ? 's' : ''} · {gradeFilter === 'K-2' ? 'Half-Day' : 'Half-Day or Full-Day'} · {gradeFilter}</p>
            {gradeFilter === 'K-2' ? (
              <>
                <p className="text-rsc-yellow text-sm font-bold mt-2">Half-Day Camps — Mon–Fri — AM: 9:00 AM – 12:00 PM</p>
                <p className="text-emerald-400 text-sm font-bold mt-1">Book a single session (flexible) or a full week of sessions and save ($100)</p>
              </>
            ) : (
              <>
                <p className="text-rsc-yellow text-sm font-bold mt-2">Morning Camp: 9:00 AM – 12:00 PM &nbsp;|&nbsp; Afternoon Camp: 1:00 PM – 4:00 PM &nbsp;|&nbsp; Full-Day Camp: 9:00 AM – 4:00 PM includes Lunch break 12:00 – 1:00 PM</p>
                <p className="text-amber-400 text-sm font-bold mt-1">Parents are responsible for providing lunch. Ready Set STEM does not provide meals.</p>
              </>
            )}
          </div>

          {/* Grade-band filter — primary pill picker so parents quickly narrow
              to their student's age range (K-2 is half-day AM only, 3-5 and 6-8
              support both AM and PM). */}
          {!loading && (
            <div className="max-w-5xl mx-auto mb-4 flex flex-wrap items-center justify-center gap-2" data-testid="grade-band-filter">
              <span className="text-[11px] font-bold text-white/40 uppercase tracking-wider mr-1">Filter by Grade</span>
              {[
                { key: 'K-2', label: 'Grades K–2', sub: 'Half-Day AM only' },
                { key: '3-5', label: 'Grades 3–5', sub: 'AM · PM · Full-Day' },
                { key: '6-8', label: 'Grades 6–8', sub: 'AM · PM · Full-Day' },
              ].map((g) => {
                const active = gradeFilter === g.key;
                return (
                  <button
                    key={g.key}
                    type="button"
                    onClick={() => toggleGradeFilter(g.key)}
                    className={`px-4 py-2 rounded-full border transition-all text-left ${
                      active
                        ? 'bg-rsc-yellow text-rsc-navy border-rsc-yellow shadow-lg shadow-rsc-yellow/20'
                        : 'bg-white/[0.04] text-white/80 border-white/15 hover:bg-white/[0.08] hover:border-white/30'
                    }`}
                    data-testid={`grade-filter-${g.key}`}
                    aria-pressed={active}
                  >
                    <div className={`text-sm font-extrabold leading-tight ${active ? 'text-rsc-navy' : 'text-white'}`}>{g.label}</div>
                    <div className={`text-[10px] leading-tight ${active ? 'text-rsc-navy/70' : 'text-white/50'}`}>{g.sub}</div>
                  </button>
                );
              })}
            </div>
          )}

          {/* Global default-location picker — sets the default selected
              location for EVERY camp's chip bar below, so a parent who
              knows they want one venue can pick it once instead of
              clicking it on each weekly card. Disabled chips reflect grade
              compatibility (K-2 only runs at Naperville & Aurora). */}
          {!loading && (
            <div className="max-w-5xl mx-auto mb-6 flex flex-wrap items-center justify-center gap-2" data-testid="global-location-picker">
              <span className="text-[11px] font-bold text-white/40 uppercase tracking-wider mr-1">Default Location</span>
              {[
                { key: 'Naperville',    label: 'Naperville',    sub: 'Mon–Fri',          restricted: false },
                { key: 'Aurora',        label: 'Aurora',        sub: 'Mon–Fri',          restricted: false },
                { key: 'Bednarcik',     label: 'Bednarcik JHS', sub: 'Mon–Thu · SD 308', restricted: true  },
                { key: 'Plank',         label: 'Plank JHS',     sub: 'Mon–Thu · SD 308', restricted: true  },
                { key: 'YMCA',          label: 'Fry Family YMCA', sub: 'Sat–Sun',         restricted: false },
              ].map((loc) => {
                // K-2 only runs at Naperville/Aurora (weekday) and YMCA (weekend) —
                // grey out the SD 308 weekday-only schools (Bednarcik / Plank) for K-2.
                const k2Disabled = gradeFilter === 'K-2' && (loc.key === 'Bednarcik' || loc.key === 'Plank');
                const disabled = k2Disabled;
                const active = !disabled && globalDefaultLocation === loc.key;
                const disabledTitle = k2Disabled
                  ? 'K–2 students aren’t hosted at this SD 308 partner school'
                  : '';
                return (
                  <button
                    key={loc.key}
                    type="button"
                    onClick={() => {
                      if (disabled) return;
                      setGlobalDefaultLocation(prev => (prev === loc.key ? null : loc.key));
                      // Reset per-camp overrides so the new default takes effect.
                      setSelectedLocationByCamp({});
                    }}
                    disabled={disabled}
                    title={disabledTitle}
                    aria-pressed={active}
                    className={`px-3.5 py-2 rounded-full border transition-all text-left ${
                      disabled
                        ? 'bg-white/[0.02] text-white/25 border-white/10 cursor-not-allowed opacity-50'
                        : active
                          ? loc.restricted
                            ? 'bg-amber-400 text-rsc-navy border-amber-400 shadow-lg shadow-amber-400/20'
                            : 'bg-emerald-400 text-rsc-navy border-emerald-400 shadow-lg shadow-emerald-400/20'
                          : 'bg-white/[0.04] text-white/80 border-white/15 hover:bg-white/[0.08] hover:border-white/30'
                    }`}
                    data-testid={`global-location-${loc.key.toLowerCase()}`}
                  >
                    <div className={`text-sm font-extrabold leading-tight flex items-center gap-1.5 ${active ? 'text-rsc-navy' : disabled ? 'text-white/30' : 'text-white'}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${disabled ? 'bg-white/20' : loc.restricted ? 'bg-amber-300' : 'bg-emerald-300'} ${active ? 'opacity-100' : 'opacity-80'}`} />
                      {loc.label}
                    </div>
                    <div className={`text-[10px] leading-tight ${active ? 'text-rsc-navy/70' : disabled ? 'text-white/25' : 'text-white/50'}`}>
                      {loc.sub}
                    </div>
                  </button>
                );
              })}
              {globalDefaultLocation && (
                <button
                  type="button"
                  onClick={() => { setGlobalDefaultLocation(null); setSelectedLocationByCamp({}); }}
                  className="text-xs text-white/50 hover:text-white underline underline-offset-2 ml-2"
                  data-testid="global-location-clear"
                >
                  Clear default
                </button>
              )}
            </div>
          )}

          {/* Jump to a Week — quick-navigate pills placed just above the Week 1 card */}
          {!loading && filteredCamps.length > 0 && (
            <div className="max-w-5xl mx-auto mb-4 p-4 rounded-xl bg-rsc-navy/60 border border-white/10 backdrop-blur-sm" data-testid="jump-to-week">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold text-white flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-rsc-yellow" />
                  Jump to a Week
                </h3>
                <p className="text-xs text-white/40">8 weeks available</p>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-9 gap-2">
                {Array.from({ length: 8 }, (_, i) => {
                  const wn = i + 1;
                  const weekDates = ['Jun 8–12','Jun 15–19','Jun 22–26','Jun 29–Jul 3','Jul 13–17','Jul 20–24','Jul 27–31','Aug 3–7'];
                  const matchingCamp = filteredCamps.find(c => new RegExp(`Week ${wn}(\\D|$)`).test(c.name));
                  return (
                    <button
                      key={wn}
                      type="button"
                      onClick={() => {
                        if (matchingCamp) {
                          document.getElementById(`camp-${matchingCamp.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }
                      }}
                      disabled={!matchingCamp}
                      className={`p-2 rounded-lg border text-center transition-all text-xs ${
                        matchingCamp
                          ? 'border-rsc-yellow/20 bg-rsc-yellow/5 hover:border-rsc-yellow/50 hover:bg-rsc-yellow/10 cursor-pointer'
                          : 'border-white/5 opacity-30 cursor-default'
                      }`}
                      data-testid={`jump-week-${wn}`}
                    >
                      <p className="font-bold text-rsc-yellow">Week {wn}</p>
                      <p className="text-[10px] text-white/40">{weekDates[i]}</p>
                    </button>
                  );
                })}
                <div className="p-2 rounded-lg border border-red-500/20 bg-red-500/5 text-center flex flex-col items-center justify-center">
                  <p className="text-[10px] font-bold text-red-300">Jul 4</p>
                  <p className="text-[9px] text-red-300/50">No camp</p>
                </div>
              </div>
            </div>
          )}

          {loading && <div className="text-center py-12"><Loader2 className="h-8 w-8 animate-spin text-rsc-yellow mx-auto" /></div>}

          {!loading && (
            <div id="camps-section" className="space-y-4 max-w-5xl mx-auto">
              {filteredCamps.map((camp, campIndex) => {
                const detail = campDetails[camp.id];
                const days = detail?.days || [];
                const sections = detail?.sections || [];
                
                // Extract week number or weekend label
                let weekNum;
                if (camp.subcategory === 'Weekend Classes') {
                  // Extract weekend number from camp name (e.g., "Weekend 1: Jun 13-14...")
                  const weekendMatch = camp.name.match(/Weekend (\d+)/i);
                  if (weekendMatch) {
                    weekNum = `Weekend ${weekendMatch[1]}`;
                  } else {
                    // Fallback: extract from dates (parse in local TZ so Saturday "2026-06-13" doesn't slip back to 2026-06-12)
                    const firstSection = sections[0];
                    if (firstSection?.start_date) {
                      const [sy, sm, sd] = firstSection.start_date.split('-').map(Number);
                      const monthDay = `${sm}/${sd}`;
                      const weekendMap = {
                        '6/13': 1, '6/20': 2, '6/27': 3, '7/4': 4,
                        '7/18': 5, '7/25': 6, '8/1': 7, '8/8': 8
                      };
                      weekNum = `Weekend ${weekendMap[monthDay] || '?'}`;
                      void sy;
                    } else {
                      weekNum = 'Weekend';
                    }
                  }
                } else {
                  weekNum = `Week ${camp.name.match(/Week (\d+)/)?.[1] || '?'}`;
                }
                
                const isK2 = (camp.grade_bands || []).includes('K-2');
                const firstSection = sections[0];

                // Show July 4th notice between Week 3 and Week 4
                const showJuly4Notice = weekNum === '4' && campIndex > 0;

                return (
                  <div key={camp.id}>
                    {showJuly4Notice && (
                      <div className="flex items-center gap-3 p-4 rounded-xl bg-red-500/10 border border-red-500/20 mb-4" data-testid="july4-notice">
                        <span className="text-2xl">🇺🇸</span>
                        <div>
                          <p className="text-sm font-bold text-red-300">No Camp: Independence Day Week</p>
                          <p className="text-xs text-white/40">June 29 – July 3, 2026 — Enjoy the holiday! Camps resume July 6.</p>
                        </div>
                      </div>
                    )}
                    <div className="rounded-xl bg-white/[0.03] border border-white/[0.06] overflow-hidden hover:border-white/[0.12] transition-colors" data-testid={`camp-${camp.id}`} id={`camp-${camp.id}`}>
                    <div className="p-4">
                      {/* Header row */}
                      <div className="flex items-center justify-between gap-4 mb-4 p-4 -mx-4 -mt-4 rounded-t-xl bg-gradient-to-r from-rsc-yellow/10 via-rsc-yellow/5 to-transparent border-b border-rsc-yellow/15">
                        <div className="flex items-center gap-3">
                          <div className="px-3 py-2 rounded-xl bg-rsc-yellow text-rsc-navy flex items-center justify-center flex-shrink-0 shadow-lg shadow-rsc-yellow/20">
                            <span className="text-sm font-extrabold whitespace-nowrap">{weekNum}</span>
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="text-lg font-extrabold text-white">
                                {firstSection?.start_date && new Date(firstSection.start_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                {firstSection?.end_date && ` – ${new Date(firstSection.end_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`}
                              </h3>
                              {isK2 && <Badge className="bg-pink-500/15 text-pink-400 border-pink-400/20 text-sm">Parent Required for Incoming K</Badge>}
                            </div>
                            <div className="flex items-center gap-3 text-sm text-white/50 mt-0.5">
                              <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5 text-rsc-yellow/60" />
                                {isK2 ? 'AM 9am–12pm · PM 1pm–4pm' : 'AM 9am–12pm · PM 1pm–4pm · Lunch 12–1pm'}
                              </span>
                              <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5 text-rsc-yellow/60" /> {sections.length} location{sections.length !== 1 ? 's' : ''}</span>
                            </div>
                          </div>
                        </div>
                        {/* Pricing tier chips — replaces old "from $X/day" block.
                             Each chip is clickable and routes to /register with a preset
                             that the registration page honors (see RegistrationPage preset effect). */}
                        <div className="flex-shrink-0 grid grid-cols-3 gap-1.5 w-full sm:w-auto sm:max-w-[440px]" data-testid={`camp-tier-chips-${camp.id}`}>
                          {/* Half-Day */}
                          <button
                            type="button"
                            onClick={() => seedCampSelection({ campId: camp.id, presetType: 'half_day' })}
                            className="group/tier rounded-lg bg-white/[0.04] border border-white/10 hover:border-rsc-yellow/50 hover:bg-white/[0.08] px-2.5 py-2 transition-all text-center"
                            data-testid={`tier-half-day-${camp.id}`}
                          >
                            <div className="text-[10px] font-bold text-white/50 uppercase tracking-wider">Half-Day</div>
                            <div className="text-lg font-extrabold text-rsc-yellow leading-tight">$69<span className="text-[10px] text-white/50 font-normal">/session</span></div>
                            <div className="text-[10px] text-white/45">AM 9–12 · PM 1–4</div>
                          </button>
                          {/* Full-Day */}
                          <button
                            type="button"
                            onClick={() => seedCampSelection({ campId: camp.id, presetType: 'full_day' })}
                            className="group/tier rounded-lg bg-white/[0.04] border border-white/10 hover:border-rsc-yellow/50 hover:bg-white/[0.08] px-2.5 py-2 transition-all text-center"
                            data-testid={`tier-full-day-${camp.id}`}
                          >
                            <div className="text-[10px] font-bold text-white/50 uppercase tracking-wider">Full-Day</div>
                            <div className="text-lg font-extrabold text-rsc-yellow leading-tight">$59<span className="text-[10px] text-white/50 font-normal">/session</span></div>
                            <div className="text-[10px] text-white/45">9am–4pm · lunch 12–1pm</div>
                          </button>
                          {/* Full-Week — best value */}
                          <button
                            type="button"
                            onClick={() => seedCampSelection({ campId: camp.id, presetType: 'full_week' })}
                            className="group/tier rounded-lg bg-gradient-to-br from-rsc-yellow/15 to-amber-400/5 border-2 border-rsc-yellow/50 hover:border-rsc-yellow hover:from-rsc-yellow/25 px-2.5 py-2 transition-all text-center relative"
                            data-testid={`tier-full-week-${camp.id}`}
                          >
                            <div className="absolute -top-2 left-1/2 -translate-x-1/2 bg-emerald-500 text-white text-[8px] font-extrabold px-1.5 py-0 rounded-full tracking-wider whitespace-nowrap">SAVE 35%</div>
                            <div className="text-[10px] font-bold text-rsc-yellow/70 uppercase tracking-wider mt-0.5">Full-Week</div>
                            <div className="text-lg font-extrabold text-rsc-yellow leading-tight">$45<span className="text-[10px] text-white/50 font-normal">/session</span></div>
                            <div className="text-[10px] text-emerald-400 font-semibold">$450 · 10 sessions</div>
                          </button>
                        </div>
                      </div>

                      {/* Hint row — nudges parents to tap the course card for the full description
                          (activities, kit contents, grade band, etc.). The rich modal is what turns
                          browsing into enrollment, so we surface it directly above the day strip. */}
                      {/* Tip line removed Feb 2026 — replaced by explicit
                          (i) Details button next to each Register action. */}

                      {/* Location chip bar — pick WHERE first, then session cards
                          below filter to that location's available days. Replaces
                          the old "Locations · 4" block that lived below the grid. */}
                      {(() => {
                        const isWknd = camp.subcategory === 'Weekend Classes';
                        // Hide YMCA from non-weekend weeks. YMCA is a
                        // weekend-only venue, so showing it as a chip on a
                        // Mon–Fri week wastes the parent's attention. The
                        // deep-link card below the schedule grid points
                        // weekend-seekers to the matching Weekend camp.
                        const sortedLocs = [...sections]
                          .filter(s => isWknd || !s.location_name?.includes('YMCA'))
                          .sort((a, b) => {
                            const order = (s) => s.location_name?.includes('Naperville') ? 0 : s.location_name?.includes('Aurora') ? 1 : s.location_name?.includes('Bednarcik') ? 2 : s.location_name?.includes('Plank') ? 3 : 4;
                            return order(a) - order(b);
                          });
                        const isRestricted = (s) => s.location_name?.includes('Bednarcik') || s.location_name?.includes('Plank') || (s.location_eligibility && s.location_eligibility !== 'open');
                        const chosenSec = getSelectedSectionForCamp(camp);
                        return (
                          <div className="mb-3" data-testid={`location-chips-${camp.id}`}>
                            <div className="flex items-center justify-between mb-1.5 sm:mb-2">
                              <div className="text-xs sm:text-sm font-bold text-white/85 uppercase tracking-wider flex items-center gap-1.5 sm:gap-2">
                                <MapPin className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-rsc-yellow" /> Pick your location
                              </div>
                              <div className="text-[10px] sm:text-xs font-semibold text-white/55">{sortedLocs.length} location{sortedLocs.length !== 1 ? 's' : ''}</div>
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                              {sortedLocs.map(sec => {
                                const restricted = isRestricted(sec);
                                const is4Day = sec.location_name?.includes('Bednarcik') || sec.location_name?.includes('Plank');
                                const daysLabel = is4Day ? 'Mon–Thu' : isWknd ? 'Sat–Sun' : 'Mon–Fri';
                                const soldOut = sec.spots_remaining === 0;
                                const showSeats = typeof sec.spots_remaining === 'number' && sec.spots_remaining > 0 && sec.spots_remaining <= 5;
                                const isActive = chosenSec?.id === sec.id;
                                return (
                                  <button
                                    type="button"
                                    key={sec.id}
                                    onClick={() => !soldOut && setSelectedLocation(camp, sec.id)}
                                    disabled={soldOut}
                                    aria-pressed={isActive}
                                    className={`group relative inline-flex items-center gap-1 sm:gap-2 rounded-full pl-2 pr-2 py-1.5 sm:pl-2.5 sm:pr-3 sm:py-2 text-sm sm:text-base font-bold transition-all border-2 ${
                                      soldOut
                                        ? 'bg-white/5 border-white/10 text-white/30 cursor-not-allowed'
                                        : isActive
                                        ? restricted
                                          ? 'bg-amber-500/20 border-amber-400 text-amber-50 shadow-[0_0_0_3px_rgba(251,191,36,0.22)]'
                                          : 'bg-emerald-500/20 border-emerald-400 text-emerald-50 shadow-[0_0_0_3px_rgba(52,211,153,0.22)]'
                                        : restricted
                                          ? 'bg-amber-500/[0.06] border-amber-500/30 text-white/85 hover:border-amber-400/70 hover:bg-amber-500/12'
                                          : 'bg-emerald-500/[0.06] border-emerald-500/30 text-white/85 hover:border-emerald-400/70 hover:bg-emerald-500/12'
                                    }`}
                                    data-testid={`location-chip-${sec.id}`}
                                  >
                                    <span className={`w-2 h-2 rounded-full ${restricted ? 'bg-amber-400' : 'bg-emerald-400'} ${isActive ? '' : 'opacity-70'}`} />
                                    <span className="leading-none">{sec.location_name?.replace('Ready Set STEM - ', '').replace('Junior High School', 'JHS')}</span>
                                    {/* Days label inline ONLY for non-restricted chips — restricted (SD 308) chips
                                        keep their badge instead, since the days are spelled out in the
                                        selected-location sub-line below the chip bar. Saves horizontal
                                        space so all 4 chips fit on one row at desktop widths. */}
                                    {!restricted && (
                                      <span className="hidden sm:inline text-xs font-semibold opacity-65 leading-none">· {daysLabel}</span>
                                    )}
                                    {restricted && (
                                      <span className="text-[10px] font-extrabold uppercase tracking-wider text-amber-200 bg-amber-500/25 px-1 py-0.5 rounded leading-none whitespace-nowrap">SD 308 ONLY</span>
                                    )}
                                    {showSeats && !soldOut && (
                                      <span className="text-[10px] font-extrabold text-red-200 bg-red-500/25 px-1 py-0.5 rounded leading-none">{sec.spots_remaining} left</span>
                                    )}
                                    {soldOut && (
                                      <span className="text-[10px] font-extrabold uppercase text-white/45 leading-none">Sold out</span>
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                            {chosenSec && (
                              <div className="text-sm text-white/55 font-medium mt-2 ml-1">
                                {chosenSec.start_date && new Date(chosenSec.start_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                {chosenSec.end_date && ` – ${new Date(chosenSec.end_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`}
                                {' · '}
                                {chosenSec.location_name?.includes('Bednarcik') || chosenSec.location_name?.includes('Plank')
                                  ? 'Mon–Thu (4 days · SD 308 students only)'
                                  : 'Mon–Fri'}
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      {/* "Tap to add" hint + single $/session label.
                          Replaces the per-card "$69 Register" buttons we removed
                          when sessions became selectable cards. */}
                      <div className="flex flex-wrap items-center justify-between gap-1.5 sm:gap-3 mb-2 sm:mb-3 px-1">
                        <div className="text-[11px] sm:text-base text-rsc-yellow font-extrabold flex items-center gap-1.5 sm:gap-2 bg-rsc-yellow/15 border-2 border-rsc-yellow/40 rounded-full px-2 py-1 sm:px-4 sm:py-2 shadow-[0_0_0_3px_rgba(255,200,0,0.10)]">
                          <CheckCircle2 className="w-3 h-3 sm:w-4 sm:h-4 text-rsc-yellow" />
                          <span>Tap a session to add</span>
                        </div>
                        <div className="text-right">
                          <div className="text-[9px] sm:text-[10px] font-bold text-white/45 uppercase tracking-wider mb-0.5">Price per session</div>
                          <div className="text-[11px] sm:text-sm font-semibold text-white/65">
                            <span className="text-white font-extrabold text-xs sm:text-base">${isK2 ? '69' : '69'}</span>
                            <span className="text-white/45">/Half</span>
                            <span className="text-white/30 mx-1">·</span>
                            <span className="text-white font-extrabold text-xs sm:text-base">$59</span>
                            <span className="text-white/45">/Full-Day</span>
                            <span className="text-white/30 mx-1">·</span>
                            <span className="text-rsc-yellow font-extrabold text-xs sm:text-base">$45</span>
                            <span className="text-rsc-yellow/70">/Full-Wk</span>
                          </div>
                        </div>
                      </div>

                      {/* 5-day strip — group by day_number, show AM/PM sessions */}
                      <div className="grid grid-cols-1 sm:grid-cols-5 gap-1.5 mb-3">
                        {(() => {
                          // Filter days based on the chip-selected location:
                          // Bednarcik / Plank are 4-day (Mon–Thu) — drop day 5.
                          const chosenSec = getSelectedSectionForCamp(camp);
                          const is4DayLoc = chosenSec?.location_name?.includes('Bednarcik') || chosenSec?.location_name?.includes('Plank');
                          const maxDayForLoc = is4DayLoc ? 4 : (camp.subcategory === 'Weekend Classes' ? 2 : 5);

                          // Group days by day_number
                          const dayGroups = {};
                          days.forEach(day => {
                            if (day.day_number > maxDayForLoc) return;
                            if (!dayGroups[day.day_number]) {
                              dayGroups[day.day_number] = [];
                            }
                            dayGroups[day.day_number].push(day);
                          });
                          
                          // Determine day names based on camp type
                          const isWeekendCamp = camp.subcategory === 'Weekend Classes';
                          const fullDayNames = isWeekendCamp 
                            ? ['Saturday', 'Sunday'] 
                            : ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
                          const emojis = { science: '🔬', coding: '💻', technology: '💻', engineering: '⚙️', math: '🔢', robotics: '🤖' };
                          
                          // Urgency: camp is "Filling Fast" if any section has <= 5 spots, or total remaining < 30% of capacity
                          const totalCapacity = sections.reduce((sum, s) => sum + (s.capacity || 0), 0);
                          const totalRemaining = sections.reduce((sum, s) => sum + (s.spots_remaining || 0), 0);
                          const hasLowSection = sections.some(s => (s.spots_remaining || 0) > 0 && (s.spots_remaining || 0) <= 5);
                          const isFillingFast = hasLowSection || (totalCapacity > 0 && totalRemaining / totalCapacity < 0.3);
                          
                          return Object.entries(dayGroups).map(([dayNum, daySessions]) => {
                            const dayIndex = parseInt(dayNum) - 1;
                            const dayName = isWeekendCamp
                              ? (dayIndex === 0 ? 'Saturday' : 'Sunday')
                              : (fullDayNames[dayIndex] || `Day ${dayNum}`);
                            
                            // Get AM and PM sessions
                            const amSession = daySessions.find(d => d.session_type === 'am');
                            const pmSession = daySessions.find(d => d.session_type === 'pm');
                            const fullDaySession = daySessions.find(d => d.session_type === 'full_day');
                            
                            // Use AM session for display, or full_day if no AM
                            const primarySession = amSession || fullDaySession || daySessions[0];
                            const cat = primarySession?.course_category || 'science';
                            const config = CATEGORY_CONFIG[cat] || CATEGORY_CONFIG.science;
                            
                            // Clean title - remove prefix like "W1 Mon AM:"
                            const cleanTitle = (title) => (title || '').replace(/^W\d+\s*(Mon|Tue|Wed|Thu|Fri|Sat|Sun|Weekend)\s*(AM|PM)?:\s*/i, '').replace(/\s*\(.*\)$/, '');
                            
                            return (
                              <div key={dayNum} className="rounded-lg bg-white/[0.04] border border-white/[0.08] p-1.5 sm:p-3.5 hover:border-rsc-yellow/40 hover:bg-white/[0.07] transition-all flex flex-col">
                                {/* Themed day header — highlights the weekly STEM rotation
                                    (Mon=Science, Tue=Tech, Wed=Eng, Thu=Math, Fri=Robotics).
                                    Uses the primary session's actual category when set,
                                    falling back to the fixed weekday→theme map. */}
                                {(() => {
                                  const themeKey = CATEGORY_CONFIG[cat] ? cat : (DAY_THEME[parseInt(dayNum)] || 'science');
                                  const theme = CATEGORY_CONFIG[themeKey];
                                  const ThemeIcon = theme.icon;
                                  return (
                                    <div className={`-mx-1.5 -mt-1.5 mb-1.5 px-2 py-1 sm:-mx-3.5 sm:-mt-3.5 sm:mb-3 sm:px-3.5 sm:py-2 rounded-t-lg bg-gradient-to-r ${theme.color} relative overflow-hidden`}>
                                      {/* Subtle sparkle texture */}
                                      <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(circle at 20% 30%, rgba(255,255,255,0.4) 1px, transparent 1px), radial-gradient(circle at 70% 60%, rgba(255,255,255,0.3) 1px, transparent 1px)', backgroundSize: '20px 20px, 30px 30px' }} />
                                      <div className="relative flex items-center justify-between gap-2">
                                        <div className="min-w-0 flex items-center gap-1.5 sm:block">
                                          <div className="text-[10px] font-bold text-white/90 uppercase tracking-[0.15em] leading-none">{dayName}</div>
                                          <div className="flex items-center gap-1 sm:gap-1.5 sm:mt-1">
                                            <ThemeIcon className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-white flex-shrink-0" />
                                            <span className="text-[11px] sm:text-sm font-extrabold text-white tracking-wide leading-none">{theme.label}</span>
                                          </div>
                                        </div>
                                        {isFillingFast && (
                                          <div className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-red-500/80 border border-white/40 animate-pulse flex-shrink-0" title="Filling fast — book soon">
                                            <Flame className="w-2.5 h-2.5 text-white flex-shrink-0" />
                                            <span className="text-[9px] font-extrabold text-white uppercase tracking-wide">Hot</span>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })()}
                                
                                {/* Mobile: stacks AM/PM vertically so each row can fit a
                                    thumbnail + Details affordance without crushing the title.
                                    sm:+ keeps the original single-column vertical layout. */}
                                <div className="grid grid-cols-1 gap-1 sm:gap-0">
                                {/* Show AM session — clickable card; tapping toggles selection */}
                                {amSession && (() => {
                                  const isSelected =
                                    campSessions[camp.id]?.[dayNum] === 'am' ||
                                    campSessions[camp.id]?.[dayNum] === 'full_day';
                                  const inCartVal = cartedSessionsByCamp[camp.id]?.[dayNum];
                                  const isInCart = !isSelected && (inCartVal === 'am' || inCartVal === 'full_day');
                                  return (
                                  <div className="mb-1 sm:mb-2.5">
                                    <div
                                      role="button"
                                      tabIndex={0}
                                      onClick={() => {
                                        // Allow re-selection even when the session is already
                                        // in cart — the parent may want to add the same week
                                        // for another learner. The green "in cart" pill stays
                                        // visible so they know it's already booked for one kid.
                                        toggleHalfSession(camp, dayNum, 'am');
                                      }}
                                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleHalfSession(camp, dayNum, 'am'); } }}
                                      className={`relative cursor-pointer text-left group/course block w-full rounded-lg p-1 -m-1 sm:p-1.5 sm:-m-1.5 transition-all ${isSelected ? 'bg-rsc-yellow/10 ring-2 ring-rsc-yellow' : isInCart ? 'bg-emerald-500/10 ring-2 ring-emerald-400/70' : 'hover:bg-rsc-yellow/[0.06] ring-2 ring-transparent hover:ring-rsc-yellow/30'}`}
                                      aria-pressed={isSelected || isInCart}
                                      aria-label={`${isInCart ? 'Already in cart:' : isSelected ? 'Deselect' : 'Select'} ${cleanTitle(amSession.course_title)} (Day ${dayNum} AM)`}
                                      data-testid={`course-card-am-${dayNum}`}
                                    >
                                      {/* Checkbox indicator — bright unselected ring + hover '+' icon, green check when carted */}
                                      <div
                                        className={`absolute top-1 right-1 z-10 w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center transition-all ${isSelected ? 'bg-rsc-yellow shadow-lg shadow-rsc-yellow/40 ring-2 ring-rsc-navy/20' : isInCart ? 'bg-emerald-400 shadow-lg shadow-emerald-400/40 ring-2 ring-rsc-navy/20' : 'bg-rsc-navy/60 ring-2 ring-white/55 group-hover/course:ring-rsc-yellow group-hover/course:bg-rsc-yellow/20'}`}
                                        data-testid={`check-am-${dayNum}`}
                                      >
                                        {isSelected ? (
                                          <CheckCircle2 className="w-5 h-5 text-rsc-navy" strokeWidth={3} />
                                        ) : isInCart ? (
                                          <CheckCircle2 className="w-5 h-5 text-rsc-navy" strokeWidth={3} />
                                        ) : (
                                          <Plus className="w-3.5 h-3.5 text-white/0 group-hover/course:text-rsc-yellow transition-colors" strokeWidth={3} />
                                        )}
                                      </div>
                                      {isInCart && (
                                        <button
                                          type="button"
                                          onClick={(e) => { e.stopPropagation(); removeOneDayFromCart(camp, dayNum, 'am'); }}
                                          className="absolute top-1 left-1 z-10 inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-wider px-2 py-1.5 min-h-[32px] rounded bg-emerald-400/95 text-rsc-navy shadow hover:bg-red-400 hover:text-white transition-colors group/pill"
                                          aria-label={`Remove ${cleanTitle(amSession.course_title)} (Day ${dayNum} AM) from cart`}
                                          data-testid={`in-cart-am-${dayNum}`}
                                        >
                                          <span>In Cart</span>
                                          <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-rsc-navy/15 group-hover/pill:bg-white/25"><X className="w-3 h-3" strokeWidth={3.5} /></span>
                                        </button>
                                      )}
                                      <div className={`inline-flex items-center gap-1 text-[11px] sm:text-xs font-extrabold px-2 py-0.5 rounded mb-1 sm:mb-1.5 ${CATEGORY_CONFIG[amSession.course_category]?.bg || config.bg} ${CATEGORY_CONFIG[amSession.course_category]?.text || config.text}`} data-testid={`time-am-${dayNum}`}>
                                        <span>AM</span>
                                        <span className="opacity-80 font-bold">{formatSessionRange(amSession)}</span>
                                      </div>
                                      {/* Body: horizontal row on mobile (thumb left | title right),
                                          vertical column on sm:+ (thumb top, title bottom). */}
                                      <div className="flex flex-row items-center gap-2 sm:block sm:gap-0">
                                        <div className={`relative shrink-0 w-12 h-12 sm:w-full sm:h-auto sm:aspect-[4/3] rounded-md bg-gradient-to-br ${CATEGORY_CONFIG[amSession.course_category]?.color || config.color} flex items-center justify-center overflow-hidden ring-0 sm:mb-1.5 transition-all`}>
                                          {amSession.thumbnail_url ? (
                                            <img src={amSession.thumbnail_url} alt="" className="absolute inset-0 w-full h-full object-cover transition-transform duration-300" onError={(e) => { e.target.style.display = 'none'; }} />
                                          ) : (
                                            <span className="text-base sm:text-2xl">{emojis[amSession.course_category] || '📚'}</span>
                                          )}
                                        </div>
                                        <p className="flex-1 min-w-0 text-sm font-bold text-white leading-snug line-clamp-2 transition-colors mb-0 sm:mb-1" title={cleanTitle(amSession.course_title)}>{cleanTitle(amSession.course_title)}</p>
                                      </div>
                                    </div>
                                    <button
                                      type="button"
                                      className="w-full text-[10px] font-semibold text-white/70 hover:text-rsc-yellow mt-1 sm:mt-1.5 flex items-center justify-center gap-1"
                                      onClick={(e) => { e.stopPropagation(); openCourseDetail(amSession.course); }}
                                      data-testid={`details-am-${dayNum}`}
                                    >
                                      <Info className="w-3 h-3" /> Details
                                    </button>
                                  </div>
                                  );
                                })()}
                                
                                {/* Show PM session — clickable card; tapping toggles selection.
                                    Hidden when the K-2 grade filter is active (K-2 is half-day AM only). */}
                                {pmSession && gradeFilter !== 'K-2' && (() => {
                                  const isSelected =
                                    campSessions[camp.id]?.[dayNum] === 'pm' ||
                                    campSessions[camp.id]?.[dayNum] === 'full_day';
                                  const inCartVal = cartedSessionsByCamp[camp.id]?.[dayNum];
                                  const isInCart = !isSelected && (inCartVal === 'pm' || inCartVal === 'full_day');
                                  return (
                                  <div className="mb-1 sm:mb-2.5">
                                    <div
                                      role="button"
                                      tabIndex={0}
                                      onClick={() => {
                                        // See AM card comment — re-selection is allowed so
                                        // sibling enrolment works without removing from cart first.
                                        toggleHalfSession(camp, dayNum, 'pm');
                                      }}
                                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleHalfSession(camp, dayNum, 'pm'); } }}
                                      className={`relative cursor-pointer text-left group/course block w-full rounded-lg p-1 -m-1 sm:p-1.5 sm:-m-1.5 transition-all ${isSelected ? 'bg-rsc-yellow/10 ring-2 ring-rsc-yellow' : isInCart ? 'bg-emerald-500/10 ring-2 ring-emerald-400/70' : 'hover:bg-rsc-yellow/[0.06] ring-2 ring-transparent hover:ring-rsc-yellow/30'}`}
                                      aria-pressed={isSelected || isInCart}
                                      aria-label={`${isInCart ? 'Already in cart:' : isSelected ? 'Deselect' : 'Select'} ${cleanTitle(pmSession.course_title)} (Day ${dayNum} PM)`}
                                      data-testid={`course-card-pm-${dayNum}`}
                                    >
                                      <div
                                        className={`absolute top-1 right-1 z-10 w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center transition-all ${isSelected ? 'bg-rsc-yellow shadow-lg shadow-rsc-yellow/40 ring-2 ring-rsc-navy/20' : isInCart ? 'bg-emerald-400 shadow-lg shadow-emerald-400/40 ring-2 ring-rsc-navy/20' : 'bg-rsc-navy/60 ring-2 ring-white/55 group-hover/course:ring-rsc-yellow group-hover/course:bg-rsc-yellow/20'}`}
                                        data-testid={`check-pm-${dayNum}`}
                                      >
                                        {isSelected ? (
                                          <CheckCircle2 className="w-5 h-5 text-rsc-navy" strokeWidth={3} />
                                        ) : isInCart ? (
                                          <CheckCircle2 className="w-5 h-5 text-rsc-navy" strokeWidth={3} />
                                        ) : (
                                          <Plus className="w-3.5 h-3.5 text-white/0 group-hover/course:text-rsc-yellow transition-colors" strokeWidth={3} />
                                        )}
                                      </div>
                                      {isInCart && (
                                        <button
                                          type="button"
                                          onClick={(e) => { e.stopPropagation(); removeOneDayFromCart(camp, dayNum, 'pm'); }}
                                          className="absolute top-1 left-1 z-10 inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-wider px-2 py-1.5 min-h-[32px] rounded bg-emerald-400/95 text-rsc-navy shadow hover:bg-red-400 hover:text-white transition-colors group/pill"
                                          aria-label={`Remove ${cleanTitle(pmSession.course_title)} (Day ${dayNum} PM) from cart`}
                                          data-testid={`in-cart-pm-${dayNum}`}
                                        >
                                          <span>In Cart</span>
                                          <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-rsc-navy/15 group-hover/pill:bg-white/25"><X className="w-3 h-3" strokeWidth={3.5} /></span>
                                        </button>
                                      )}
                                      <div className={`inline-flex items-center gap-1 text-[11px] sm:text-xs font-extrabold px-2 py-0.5 rounded mb-1 sm:mb-1.5 ${CATEGORY_CONFIG[pmSession.course_category]?.bg || config.bg} ${CATEGORY_CONFIG[pmSession.course_category]?.text || config.text}`} data-testid={`time-pm-${dayNum}`}>
                                        <span>PM</span>
                                        <span className="opacity-80 font-bold">{formatSessionRange(pmSession)}</span>
                                      </div>
                                      <div className="flex flex-row items-center gap-2 sm:block sm:gap-0">
                                        <div className={`relative shrink-0 w-12 h-12 sm:w-full sm:h-auto sm:aspect-[4/3] rounded-md bg-gradient-to-br ${CATEGORY_CONFIG[pmSession.course_category]?.color || config.color} flex items-center justify-center overflow-hidden ring-0 sm:mb-1.5 transition-all`}>
                                          {pmSession.thumbnail_url ? (
                                            <img src={pmSession.thumbnail_url} alt="" className="absolute inset-0 w-full h-full object-cover transition-transform duration-300" onError={(e) => { e.target.style.display = 'none'; }} />
                                          ) : (
                                            <span className="text-base sm:text-2xl">{emojis[pmSession.course_category] || '📚'}</span>
                                          )}
                                        </div>
                                        <p className="flex-1 min-w-0 text-sm font-bold text-white leading-snug line-clamp-2 transition-colors mb-0 sm:mb-1" title={cleanTitle(pmSession.course_title)}>{cleanTitle(pmSession.course_title)}</p>
                                      </div>
                                    </div>
                                    <button
                                      type="button"
                                      className="w-full text-[10px] font-semibold text-white/70 hover:text-rsc-yellow mt-1 sm:mt-1.5 flex items-center justify-center gap-1"
                                      onClick={(e) => { e.stopPropagation(); openCourseDetail(pmSession.course); }}
                                      data-testid={`details-pm-${dayNum}`}
                                    >
                                      <Info className="w-3 h-3" /> Details
                                    </button>
                                  </div>
                                  );
                                })()}
                                </div>
                                
                                {/* Show full-day session if no AM/PM (K-2 + Weekend Camps).
                                    Tapping the card toggles full_day for that day. */}
                                {!amSession && !pmSession && fullDaySession && (() => {
                                  const isSelected = campSessions[camp.id]?.[dayNum] === 'full_day';
                                  const isInCart = !isSelected && cartedSessionsByCamp[camp.id]?.[dayNum] === 'full_day';
                                  return (
                                  <div className="mb-1 sm:mb-2.5">
                                    <div
                                      role="button"
                                      tabIndex={0}
                                      onClick={() => {
                                        // See AM card comment — re-selection allowed for sibling enrolments.
                                        toggleFullDay(camp, dayNum);
                                      }}
                                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleFullDay(camp, dayNum); } }}
                                      className={`relative cursor-pointer text-left group/course block w-full rounded-lg p-1 -m-1 sm:p-1.5 sm:-m-1.5 transition-all ${isSelected ? 'bg-rsc-yellow/10 ring-2 ring-rsc-yellow' : isInCart ? 'bg-emerald-500/10 ring-2 ring-emerald-400/70' : 'hover:bg-rsc-yellow/[0.06] ring-2 ring-transparent hover:ring-rsc-yellow/30'}`}
                                      aria-pressed={isSelected || isInCart}
                                      aria-label={`${isInCart ? 'Already in cart:' : isSelected ? 'Deselect' : 'Select'} ${cleanTitle(fullDaySession.course_title)} (Day ${dayNum})`}
                                      data-testid={`course-card-fullday-${dayNum}`}
                                    >
                                      <div
                                        className={`absolute top-1 right-1 z-10 w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center transition-all ${isSelected ? 'bg-rsc-yellow shadow-lg shadow-rsc-yellow/40 ring-2 ring-rsc-navy/20' : isInCart ? 'bg-emerald-400 shadow-lg shadow-emerald-400/40 ring-2 ring-rsc-navy/20' : 'bg-rsc-navy/60 ring-2 ring-white/55 group-hover/course:ring-rsc-yellow group-hover/course:bg-rsc-yellow/20'}`}
                                        data-testid={`check-fullday-${dayNum}`}
                                      >
                                        {isSelected ? (
                                          <CheckCircle2 className="w-5 h-5 text-rsc-navy" strokeWidth={3} />
                                        ) : isInCart ? (
                                          <CheckCircle2 className="w-5 h-5 text-rsc-navy" strokeWidth={3} />
                                        ) : (
                                          <Plus className="w-3.5 h-3.5 text-white/0 group-hover/course:text-rsc-yellow transition-colors" strokeWidth={3} />
                                        )}
                                      </div>
                                      {isInCart && (
                                        <button
                                          type="button"
                                          onClick={(e) => { e.stopPropagation(); removeOneDayFromCart(camp, dayNum, 'full_day'); }}
                                          className="absolute top-1 left-1 z-10 inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-wider px-2 py-1.5 min-h-[32px] rounded bg-emerald-400/95 text-rsc-navy shadow hover:bg-red-400 hover:text-white transition-colors group/pill"
                                          aria-label={`Remove ${cleanTitle(fullDaySession.course_title)} (Day ${dayNum}) from cart`}
                                          data-testid={`in-cart-fullday-${dayNum}`}
                                        >
                                          <span>In Cart</span>
                                          <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-rsc-navy/15 group-hover/pill:bg-white/25"><X className="w-3 h-3" strokeWidth={3.5} /></span>
                                        </button>
                                      )}
                                      <div className={`inline-flex items-center gap-1 text-[11px] sm:text-xs font-extrabold px-2 py-0.5 rounded mb-1 sm:mb-1.5 ${config.bg} ${config.text}`} data-testid={`time-fullday-${dayNum}`}>
                                        <span>{config.label.toUpperCase()}</span>
                                        <span className="opacity-80 font-bold">{formatSessionRange(fullDaySession)}</span>
                                      </div>
                                      <div className="flex flex-row items-center gap-2 sm:block sm:gap-0">
                                        <div className={`relative shrink-0 w-12 h-12 sm:w-full sm:h-auto sm:aspect-[4/3] rounded-md bg-gradient-to-br ${config.color} flex items-center justify-center overflow-hidden ring-0 sm:mb-1.5 transition-all`}>
                                          {fullDaySession.thumbnail_url ? (
                                            <img src={fullDaySession.thumbnail_url} alt="" className="absolute inset-0 w-full h-full object-cover transition-transform duration-300" onError={(e) => { e.target.style.display = 'none'; }} />
                                          ) : (
                                            <span className="text-base sm:text-2xl">{emojis[cat] || '📚'}</span>
                                          )}
                                        </div>
                                        <p className="flex-1 min-w-0 text-sm font-bold text-white leading-snug line-clamp-2 transition-colors mb-0 sm:mb-1" title={cleanTitle(fullDaySession.course_title)}>{cleanTitle(fullDaySession.course_title)}</p>
                                      </div>
                                    </div>
                                    <button
                                      type="button"
                                      className="w-full text-[10px] font-semibold text-white/70 hover:text-rsc-yellow mt-1 sm:mt-1.5 flex items-center justify-center gap-1"
                                      onClick={(e) => { e.stopPropagation(); openCourseDetail(fullDaySession.course); }}
                                      data-testid={`details-fullday-${dayNum}`}
                                    >
                                      <Info className="w-3 h-3" /> Details
                                    </button>
                                  </div>
                                  );
                                })()}
                                
                                <div className="mt-auto" />
                                
                              </div>
                            );
                          });
                        })()}
                      </div>

                      {/* YMCA weekend deep-link card — only shown on weekday
                          camps, when the matching same-week Weekend camp
                          (subcategory='Weekend Classes') exists with a YMCA
                          section. Surfaces the weekend option without
                          cluttering the location chip bar above. */}
                      {(() => {
                        if (camp.subcategory === 'Weekend Classes') return null;
                        const myStart = sections[0]?.start_date;
                        if (!myStart) return null;
                        const start = new Date(myStart + 'T00:00:00');
                        // Match weekend camps whose section starts within the
                        // 7-day window of THIS camp's section start (covers the
                        // weekend immediately preceding or following the week).
                        const matchingWeekend = filteredCamps.find(c => {
                          if (c.subcategory !== 'Weekend Classes') return false;
                          const wkSection = (campDetails[c.id]?.sections || [])[0];
                          if (!wkSection?.start_date) return false;
                          const wkStart = new Date(wkSection.start_date + 'T00:00:00');
                          const diff = Math.abs(wkStart - start) / (1000 * 60 * 60 * 24);
                          return diff <= 6;
                        });
                        if (!matchingWeekend) return null;
                        const wkSections = campDetails[matchingWeekend.id]?.sections || [];
                        const ymcaSec = wkSections.find(s => s.location_name?.includes('YMCA'));
                        if (!ymcaSec) return null;
                        const wkStart = new Date(wkSections[0].start_date + 'T00:00:00');
                        const wkEnd = wkSections[0].end_date ? new Date(wkSections[0].end_date + 'T00:00:00') : null;
                        const fmt = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                        const dateRange = wkEnd ? `${fmt(wkStart)}–${fmt(wkEnd)}` : fmt(wkStart);
                        return (
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedLocation(matchingWeekend, ymcaSec.id);
                              setTimeout(() => {
                                document.getElementById(`camp-${matchingWeekend.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                              }, 80);
                            }}
                            className="w-full mb-3 group flex items-center justify-between gap-3 rounded-xl bg-gradient-to-r from-sky-500/10 to-cyan-500/5 border border-sky-400/30 hover:border-sky-300/60 hover:from-sky-500/15 px-4 py-2.5 transition-all"
                            data-testid={`ymca-weekend-link-${camp.id}`}
                          >
                            <div className="flex items-center gap-3 text-left">
                              <div className="w-8 h-8 rounded-full bg-sky-400/20 border border-sky-400/40 flex items-center justify-center flex-shrink-0">
                                <span className="text-base" role="img" aria-label="Calendar">📅</span>
                              </div>
                              <div>
                                <div className="text-sm font-bold text-sky-100 leading-tight">
                                  Looking for weekend classes? <span className="text-sky-300">YMCA-Naperville</span>
                                </div>
                                <div className="text-xs text-sky-200/70">
                                  Sat–Sun · {dateRange} · jump to {matchingWeekend.name}
                                </div>
                              </div>
                            </div>
                            <ArrowRight className="w-4 h-4 text-sky-300 group-hover:translate-x-0.5 transition-transform flex-shrink-0" />
                          </button>
                        );
                      })()}

                      {/* Per-camp selection summary + inline registration panel
                          REMOVED — session selections now accumulate across all
                          weeks into `campSessions`. The global yellow pending bar
                          (bottom of page) shows the combined total at the correct
                          cross-week tier, and the MultiWeekCartModal submits every
                          pending week at once. */}

                      {/* Grade badges */}
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex gap-1">
                          {(camp.grade_bands || []).map(g => {
                            const label = g === '3-5' ? 'Entering Gr 3 – Gr 8' : g === '6-8' ? 'Entering Gr 6 – Gr 8' : g === 'K-2' ? 'Entering K – Gr 2' : g;
                            return <Badge key={g} className="bg-rsc-yellow/15 text-rsc-yellow border-rsc-yellow/30 text-sm font-bold px-3 py-1">{label}</Badge>;
                          })}
                        </div>
                        <span className="text-sm text-white/20">{sections.length} location{sections.length !== 1 ? 's' : ''}</span>
                      </div>

                      {/* Testimonial */}
                      {campIndex < testimonials.length && (
                        <div className="flex items-start gap-3 p-3 rounded-lg bg-blue-500/5 border border-blue-500/10">
                          <div className="flex gap-0.5 mt-0.5">
                            {[...Array(5)].map((_, i) => (
                              <Star key={i} className="w-3 h-3 fill-rsc-yellow text-rsc-yellow" />
                            ))}
                          </div>
                          <div className="flex-1">
                            <p className="text-sm text-white/80 italic">"{testimonials[campIndex].quote}"</p>
                            <p className="text-xs text-white/40 mt-1">— {testimonials[campIndex].name}, {testimonials[campIndex].location}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  </div>
                );
              })}
              {filteredCamps.length === 0 && !loading && (
                <div className="max-w-xl mx-auto text-center py-10 rounded-2xl bg-white/[0.03] border border-white/10">
                  {gradeFilter === 'K-2' ? (
                    <>
                      <Baby className="w-10 h-10 text-rsc-yellow/60 mx-auto mb-3" />
                      <h3 className="text-white font-bold text-lg mb-1">K–2 Summer Camps Launching Soon</h3>
                      <p className="text-white/60 text-sm mb-4">Our Grades K–2 half-day (AM-only) camps are being finalized for Summer 2026. Check back soon or browse our current Grades 3–5 / 6–8 camps.</p>
                      <div className="flex flex-wrap items-center justify-center gap-2">
                        <button type="button" onClick={() => toggleGradeFilter('3-5')} className="px-4 py-1.5 rounded-full bg-rsc-yellow text-rsc-navy text-sm font-bold">See Grades 3–5</button>
                        <button type="button" onClick={() => toggleGradeFilter('6-8')} className="px-4 py-1.5 rounded-full bg-white/10 text-white text-sm font-bold border border-white/20 hover:bg-white/15">See Grades 6–8</button>
                      </div>
                    </>
                  ) : (
                    <p className="text-white/60 text-sm">No camps found for this grade band.</p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Bottom pricing section removed — single All-Inclusive Pricing panel now lives in the hero.
          Parents scan the hero, scroll to camps, and never have to hunt the pricing twice. */}
      {/* ═══════════ FAQ SECTION ═══════════ */}
      <section className="py-12 bg-gradient-to-b from-[#0f2847] to-rsc-navy">
        <div className="container-app max-w-4xl">
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-2">
              Frequently Asked <span className="text-rsc-yellow">Questions</span>
            </h2>
            <p className="text-white/60">Everything parents need to know</p>
          </div>

          <div className="space-y-4">
            {/* Safety */}
            <details className="group rounded-xl bg-white/[0.03] border border-white/[0.06] p-4 hover:border-white/[0.12] transition-all">
              <summary className="flex items-center justify-between cursor-pointer text-white font-bold">
                <span className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-emerald-400" />
                  Is my child safe at camp?
                </span>
                <ChevronDown className="w-5 h-5 text-white/40 group-open:rotate-180 transition-transform" />
              </summary>
              <div className="mt-3 text-sm text-white/70 space-y-2 pl-7">
                <p>Absolutely! Safety is our #1 priority:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li><strong>All staff are background checked</strong> and trained in emergency protocols</li>
                  <li><strong>1:15 student-to-instructor ratio</strong> ensures personalized attention</li>
                  <li><strong>Emergency procedures in place</strong> at all locations</li>
                  <li><strong>Secure check-in/check-out</strong> with parent verification</li>
                </ul>
              </div>
            </details>

            {/* Pricing */}
            <details className="group rounded-xl bg-white/[0.03] border border-white/[0.06] p-4 hover:border-white/[0.12] transition-all">
              <summary className="flex items-center justify-between cursor-pointer text-white font-bold">
                <span className="flex items-center gap-2">
                  <Tag className="w-5 h-5 text-rsc-yellow" />
                  What are the prices and discounts?
                </span>
                <ChevronDown className="w-5 h-5 text-white/40 group-open:rotate-180 transition-transform" />
              </summary>
              <div className="mt-3 text-sm text-white/70 space-y-2 pl-7">
                <p><strong>Pricing — tiered per session:</strong></p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>1 session (AM or PM): <strong className="text-rsc-yellow">$69</strong></li>
                  <li>2–4 sessions: <strong className="text-emerald-400">$59/session</strong></li>
                  <li>5–9 sessions: <strong className="text-emerald-400">$49/session</strong></li>
                  <li>10+ sessions (full week AM+PM): <strong className="text-emerald-400">$45/session</strong> · $450 total</li>
                </ul>
                <p className="text-[11px] text-white/50 mt-2">A "session" is one half-day (AM or PM). A full day counts as 2 sessions.</p>
                <p className="text-emerald-400 font-bold mt-3">💰 The more sessions you book, the more you save!</p>
              </div>
            </details>

            {/* What to Bring */}
            <details className="group rounded-xl bg-white/[0.03] border border-white/[0.06] p-4 hover:border-white/[0.12] transition-all">
              <summary className="flex items-center justify-between cursor-pointer text-white font-bold">
                <span className="flex items-center gap-2">
                  <Package className="w-5 h-5 text-blue-400" />
                  What should my child bring?
                </span>
                <ChevronDown className="w-5 h-5 text-white/40 group-open:rotate-180 transition-transform" />
              </summary>
              <div className="mt-3 text-sm text-white/70 space-y-2 pl-7">
                <p><strong>Required:</strong></p>
                <ul className="list-disc pl-5 space-y-1">
                  <li><strong>Lunch</strong> (Parents are responsible for providing lunch)</li>
                  <li><strong>Water bottle</strong></li>
                  <li><strong>Snacks</strong> (optional)</li>
                </ul>
                <p className="mt-3"><strong>We Provide:</strong></p>
                <ul className="list-disc pl-5 space-y-1">
                  <li><strong>All laptops and technology</strong></li>
                  <li><strong>All kits and materials</strong> (kids take home their projects!)</li>
                  <li><strong>Instruction and supervision</strong></li>
                </ul>
              </div>
            </details>

            {/* Schedule */}
            <details className="group rounded-xl bg-white/[0.03] border border-white/[0.06] p-4 hover:border-white/[0.12] transition-all">
              <summary className="flex items-center justify-between cursor-pointer text-white font-bold">
                <span className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-purple-400" />
                  What are the camp hours?
                </span>
                <ChevronDown className="w-5 h-5 text-white/40 group-open:rotate-180 transition-transform" />
              </summary>
              <div className="mt-3 text-sm text-white/70 space-y-2 pl-7">
                <p><strong>Grades 3-8 (Full Day):</strong></p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>9:00 AM - 4:00 PM</li>
                  <li>Lunch break: 12:00 PM - 1:00 PM</li>
                </ul>
                <p className="mt-3"><strong>Grades K-2 (Half Day) — choose AM or PM:</strong></p>
                <ul className="list-disc pl-5 space-y-1">
                  <li><strong>AM Session:</strong> 9:00 AM – 12:00 PM</li>
                  <li><strong>PM Session:</strong> 1:00 PM – 4:00 PM</li>
                  <li>Book a single session or multiple days — no full-week commitment</li>
                  <li>Parent presence mandatory for incoming Kindergartners only</li>
                </ul>
              </div>
            </details>

            {/* Extended Care */}
            <details className="group rounded-xl bg-white/[0.03] border border-white/[0.06] p-4 hover:border-white/[0.12] transition-all">
              <summary className="flex items-center justify-between cursor-pointer text-white font-bold">
                <span className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-emerald-400" />
                  Do you offer extended care (early drop-off / late pick-up)?
                </span>
                <ChevronDown className="w-5 h-5 text-white/40 group-open:rotate-180 transition-transform" />
              </summary>
              <div className="mt-3 text-sm text-white/70 space-y-2 pl-7">
                <p>Yes! Extended care is available for Full-Day campers who need flexible arrival and departure times.</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li><strong>Early drop-off</strong> — available before the 9:00 AM start</li>
                  <li><strong>Late pick-up</strong> — available after the 4:00 PM end</li>
                  <li><strong>Add-on price:</strong> <strong className="text-rsc-yellow">$100/week</strong></li>
                </ul>
                <p className="text-emerald-400 font-semibold mt-2">Add extended care at checkout when registering for Full-Day camps.</p>
              </div>
            </details>

            {/* Registration */}
            <details className="group rounded-xl bg-white/[0.03] border border-white/[0.06] p-4 hover:border-white/[0.12] transition-all">
              <summary className="flex items-center justify-between cursor-pointer text-white font-bold">
                <span className="flex items-center gap-2">
                  <GraduationCap className="w-5 h-5 text-rsc-yellow" />
                  How do I register?
                </span>
                <ChevronDown className="w-5 h-5 text-white/40 group-open:rotate-180 transition-transform" />
              </summary>
              <div className="mt-3 text-sm text-white/70 space-y-2 pl-7">
                <p>Registration is easy:</p>
                <ol className="list-decimal pl-5 space-y-1">
                  <li>Browse camps above and click <strong className="text-rsc-yellow">"Register"</strong> on your preferred week</li>
                  <li>Select days (mix & match AM/PM/Full Day)</li>
                  <li>Enter student and parent information</li>
                  <li>Complete payment securely online</li>
                  <li>Receive confirmation via email with all details</li>
                </ol>
                <p className="text-purple-300 mt-3">📅 <strong>Still deciding?</strong> Join us May 30 for a free preview day!</p>
              </div>
            </details>
          </div>
        </div>
      </section>

      {/* Course Detail Modal — shared component (same look as /register page) */}
      <CourseDetailModal
        state={courseDetailModal.state}
        onClose={courseDetailModal.close}
        onRegister={handleCourseRegister}
      />

      {/* ═══════════ PENDING SELECTIONS BAR (yellow) ═══════════
          Shows once the parent has toggled sessions — across one OR more camp
          weeks — but not yet added them to the cart. Totals every pending week
          and prices at the cross-week discount tier, then opens the
          MultiWeekCartModal (learner / extended care / coupon / submit). */}
      {!loading && (() => {
        const pendingSessionCount = Object.values(campSessions).reduce(
          (sum, sessions) => sum + countSessions(sessions), 0,
        );
        if (pendingSessionCount === 0) return null;
        const pendingTier = tierFor(pendingSessionCount, DEFAULT_TIERS);
        const pendingTotal = pendingSessionCount * pendingTier.price;
        return (
          <div
            className="fixed bottom-0 left-0 right-0 z-50 backdrop-blur-lg border-t shadow-2xl shadow-black/50 bg-rsc-navy/95 border-rsc-yellow/30"
            data-testid="sticky-pending-bar"
          >
            <div className="container-app flex items-center justify-between py-3 gap-3">
              <div className="min-w-0">
                <p className="text-sm font-extrabold text-white leading-tight" data-testid="sticky-pending-summary">
                  {pendingSessionCount} session{pendingSessionCount !== 1 ? 's' : ''} selected
                  <span className="text-rsc-yellow"> · ${pendingTotal.toFixed(2)}</span>
                </p>
                <p className="text-[11px] text-white/50">
                  ${pendingTier.price}/session{pendingSessionCount >= 10 ? ' · Best rate!' : ''}
                </p>
              </div>
              <Button
                onClick={() => setCartModalOpen(true)}
                className="bg-rsc-yellow text-rsc-navy font-extrabold rounded-full px-5 py-2 h-11 min-h-[44px] flex-shrink-0 hover:bg-rsc-yellow/90 shadow-lg"
                data-testid="sticky-add-to-cart-btn"
              >
                <ShoppingCart className="w-4 h-4 mr-1.5" /> Add to Cart
              </Button>
            </div>
          </div>
        );
      })()}

      {/* ═══════════ CART BAR (green) ═══════════
          Shows when the server cart has items. Stacks ABOVE the yellow pending
          bar when both are visible so neither is obscured. */}
      {!loading && (() => {
        const items = (cart?.items || []);
        if (typeof window !== 'undefined') window.__bottomBarItems = items.length;
        const itemCount = items.length;
        const sessionCount = items.reduce((n, it) => n + (it.selected_sessions?.length || 0), 0);
        const total = parseFloat(cart?.total || '0') || 0;
        if (itemCount === 0) return null;
        const hasPending = Object.values(campSessions).some(s => Object.keys(s).length > 0);
        return (
        <div
          className={`fixed left-0 right-0 z-40 backdrop-blur-lg border-t shadow-2xl shadow-black/50 bg-emerald-950/95 border-emerald-500/30 ${hasPending ? 'bottom-[64px]' : 'bottom-0'}`}
          data-testid="sticky-register-bar"
        >
          <div className="container-app flex items-center justify-between py-3 gap-3">
            <>
                <div className="flex items-center gap-3 min-w-0">
                  <div className="relative w-9 h-9 rounded-full bg-emerald-400 flex items-center justify-center flex-shrink-0 shadow-lg shadow-emerald-400/30">
                    <ShoppingCart className="w-5 h-5 text-rsc-navy" strokeWidth={2.5} />
                    <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rsc-navy text-emerald-300 text-[10px] font-extrabold flex items-center justify-center border-2 border-emerald-400" data-testid="sticky-cart-count">
                      {sessionCount || itemCount}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-extrabold text-white leading-tight truncate" data-testid="sticky-cart-summary">
                      {sessionCount} session{sessionCount !== 1 ? 's' : ''} in cart
                      <span className="text-emerald-300"> · ${total.toFixed(2)}</span>
                    </p>
                    <p className="text-[11px] text-emerald-200/80 hidden sm:block">
                      Coupons & extended-care apply at checkout
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Button
                    variant="outline"
                    onClick={() => navigate('/cart')}
                    className="border-emerald-400/40 text-white bg-white/[0.04] hover:bg-white/10 hover:border-emerald-300 font-bold rounded-full px-4 py-2 text-sm h-11 min-h-[44px]"
                    data-testid="sticky-view-cart-btn"
                  >
                    View Cart
                  </Button>
                  <Button
                    onClick={() => navigate('/cart')}
                    className="bg-emerald-400 hover:bg-emerald-300 text-rsc-navy font-extrabold rounded-full px-5 sm:px-6 py-2 text-sm h-11 min-h-[44px] shadow-lg hover:scale-[1.02] transition-all"
                    data-testid="sticky-checkout-btn"
                  >
                    Checkout <ArrowRight className="w-4 h-4 ml-1.5" />
                  </Button>
                </div>
              </>
          </div>
        </div>
        );
      })()}

      {/* Spacer — height adapts to how many sticky bars are visible. */}
      {(() => {
        const hasPending = Object.values(campSessions).some(s => Object.keys(s).length > 0);
        const hasCart = (cart?.items?.length || 0) > 0;
        if (hasPending && hasCart) return <div className="h-28" />;
        if (hasPending || hasCart) return <div className="h-16" />;
        return null;
      })()}

      {/* Multi-week cart modal — collects learner, extended care, coupon, then
          submits every pending week at once (passing companion_sessions so the
          cross-week tier discount applies). Clears campSessions on success. */}
      <MultiWeekCartModal
        open={cartModalOpen}
        onClose={() => setCartModalOpen(false)}
        campSessions={campSessions}
        camps={camps}
        campDetails={campDetails}
        getSelectedSectionForCamp={getSelectedSectionForCamp}
        onAdded={() => setCampSessions({})}
      />

      {/* Parent Concierge Modal */}
      <ParentConciergeModal
        isOpen={conciergeOpen}
        onClose={() => setConciergeOpen(false)}
        onResultsReady={handleConciergeResults}
      />
    </div>
    </TooltipProvider>
  );
}
